import React, { useEffect, useMemo } from 'react';
import ScrollContainer from '../../../Base/Components/ScrollContainer';
import { useAppContext } from '../../../../Services/AppContext';
import { EventListType } from '../../Enums/eventListType.enum';
import useFilters from '../../../Base/Hooks/useFilters';
import moment from 'moment';
import {
	Event,
	MeetingType,
	useEventListQuery,
} from '../../../../graphql/graphql';
import { useFlatDataList } from '../../../Base/Hooks/useFlatDataList';
import DataTable, {
	DataTableColumn,
} from '../../../Base/Components/DataTable/DataTable';
import FormattedMomentDate, {
	DateFormat,
} from '../../../Base/Components/FormattedMomentDate/FormattedMomentDate';
import Text from '../../../../Components/Base/Text/Text';
import styles from './EventList.module.scss';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import Loading from '../../../../Components/Loading';

interface Props {
	patientId?: string;
	fulltext?: string;
	handleShowEventInCalendar: (eventId: string) => void;
	type: EventListType;
	emptyMessage?: string;
}

const WRAPPER_ID = 'eventListWrapper';

const EventList: React.FC<Props> = ({
	fulltext,
	patientId,
	type,
	handleShowEventInCalendar,
}) => {
	const { workplaceId } = useAppContext();
	const { handleDateTimeValue } = useFilters();

	const { flatData, pagination, appendData } = useFlatDataList<Event>();

	const { data, isLoading, isFetching, refetch } = useEventListQuery({
		where: {
			fulltext,
			userIds: patientId ? [patientId] : undefined,
			workplaceIds: [workplaceId],
			cancelledEvent: type === EventListType.CANCELED,
			start:
				type === EventListType.FUTURE
					? handleDateTimeValue(moment())
					: undefined,
		},
		pagination,
	});

	useEffect(() => {
		appendData((data?.workplaceEvents?.data as Event[]) ?? []);
	}, [data]);

	useEffect(() => {
		refetch();
	}, []);

	const columns: DataTableColumn<Event, undefined>[] = useMemo(
		() => [
			{
				key: 'date',
				label: 'component.eventList.header.date',
				visible: true,
				renderItem: item => (
					<FormattedMomentDate
						date={item.dateFrom}
						format={DateFormat.DATE_TIME}
					/>
				),
			},
			{
				key: 'type',
				label: 'component.eventList.header.typeOfConsultation',
				visible: true,
				renderItem: item => {
					if (item.meetingType === MeetingType.Telemedicine) {
						return (
							<Text
								tx='base.meetingType.telemedicine'
								inline
								className={styles.heavy}
							/>
						);
					}

					if (item.meetingType === MeetingType.OnSite) {
						return (
							<Text
								tx='base.meetingType.onSite'
								inline
								className={styles.heavy}
							/>
						);
					}

					return '-';
				},
			},
			{
				key: 'caseType',
				label: 'component.eventList.header.caseType',
				visible: true,
				renderItem: item => (
					<>
						<div className={styles.heavy}>{item.caseType?.label ?? '-'}</div>
						{item.description && <div>{item.description}</div>}
					</>
				),
			},
			{
				key: 'doctor',
				label: 'component.eventList.header.doctor',
				visible: true,
				renderItem: item => (
					<>
						<div className={styles.heavy}>
							{item.doctor?.profile?.fullName ?? '-'}
						</div>
						{item?.calendar?.workplace?.doctorDisplayName && (
							<div className={styles.workplace}>
								{item?.calendar?.workplace?.doctorDisplayName}
							</div>
						)}
					</>
				),
			},
			{
				key: 'createdBy',
				label: 'component.eventList.header.createdBy',
				visible: true,
				renderItem: item => item.createdBy?.profile?.fullName ?? '-',
			},
			{
				key: 'canceledBy',
				label: 'component.eventList.header.canceledBy',
				visible: type === EventListType.CANCELED,
				renderItem: item => item.canceledBy?.profile?.fullName ?? '-',
			},
			{
				key: 'actions',
				visible: type === EventListType.FUTURE,
				renderItem: item => (
					<>
						<LinkButton
							title='base.showInCalendar'
							theme={Theme.BLUE}
							onClick={() => handleShowEventInCalendar(item.id)}
						/>
					</>
				),
			},
		],
		[type],
	);

	if (isLoading || isFetching) {
		return <Loading />;
	}

	return (
		<ScrollContainer wrapperId={WRAPPER_ID} className={styles.wrapper}>
			<DataTable<Event, undefined>
				data={flatData}
				table={undefined}
				columns={columns}
			/>
		</ScrollContainer>
	);
};

export default EventList;
